// Iter152 · OfertasComercial refactor: OfertasList, OfertaForm y KPIsYTD
// se han extraído a /pages/ofertas_comercial/*. Este archivo queda con el
// orquestador de tabs, AgendaView, ExcelVerification, UpcomingList y los
// bloques de digest emails.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import {
  FileText, Calendar, BarChart3, Sparkles, Target, TrendingUp, Clock, MapPin, Euro,
  Plus, X, Search, Filter, ChevronDown, ChevronRight, Trash2, Edit, CheckCircle2, AlertTriangle, Loader2, Check,
  Mail, Send, Eye, Bell, Settings, Globe, ArrowRight, Bot, ShoppingCart, RefreshCw, ShieldCheck, ExternalLink,
  FileSpreadsheet, Pencil, Link2, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import MercadoView from "./MercadoView";
import DGFilter, { useDGFilter, DG_LABELS } from "@/components/DGFilter";
import OfertasList from "./ofertas_comercial/OfertasList";
import KPIsYTD, { Kpi } from "./ofertas_comercial/OfertasKPIs";
import CompetitionMapView from "./ofertas_comercial/CompetitionMapView";
import FocusWeekView from "./ofertas_comercial/FocusWeekView";
import SolvenciasCatalogo from "./ofertas_comercial/SolvenciasCatalogo";
import { fmtEur, fmtMM, fmtPct, ESTADO_CLS, ACTIVIDADES_POR_DG, ALL_ACTIVIDADES } from "./ofertas_comercial/shared";
import HistoricoImportModal from "@/components/historico/HistoricoImportModal";
import ResponsablePicker from "@/components/offer/ResponsablePicker";

export default function OfertasComercial() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      const valid = ["list", "agenda", "mercado", "competition", "focus", "kpis", "upcoming", "solvencias"];
      return t && valid.includes(t) ? t : "list";
    } catch { return "list"; }
  });
  const { user } = useAuth();
  const { dg, setDg } = useDGFilter(user?.email);
  // Deep-link vivo: cuando otra vista navega a ?tab=list&open=ID (alertas de
  // Agenda / estancadas → abrir expediente) sincronizamos la pestaña activa
  // aunque la página ya esté montada.
  const location = useLocation();
  useEffect(() => {
    try {
      const qt = new URLSearchParams(location.search).get("tab");
      const valid = ["list", "agenda", "mercado", "competition", "focus", "kpis", "upcoming", "solvencias"];
      if (qt && valid.includes(qt)) setTab(qt);
    } catch { /* noop */ }
  }, [location.search]);
  return (
    <div className={`space-y-5 w-full`} data-testid="ofertas-comercial-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">{t("ofertasComercial.title")}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t("ofertasComercial.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <DGFilter value={dg} onChange={setDg} testidPrefix="oc-dg-filter"/>
        </div>
      </div>
      {dg && (
        <div className="text-[11px] text-slate-500 -mt-2" data-testid="oc-dg-active-note">
          Filtro activo: <b className="text-slate-700">{DG_LABELS[dg]}</b>. Se aplica a Licitaciones, Agenda, MERCADO, KPIs y Upcoming (sólo se muestran los docs con esa DG asignada).
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {[
          // SU07 (valoriza-verificacion-ola3-feedback-ofertas-su-2026-10-13.md)
          // · orden comercial exacto pedido: Mercado → Agenda → Licitaciones
          // → KPI → Upcoming → Competencia. Solvencias se saca de este grid
          // de seis pestañas principales (ver nota más abajo).
          { key: "mercado",     icon: ShoppingCart, label: "MERCADO" },
          { key: "agenda",      icon: Calendar,   label: "Agenda" },
          { key: "list",        icon: FileText,   label: "Licitaciones" },
          { key: "kpis",        icon: BarChart3,  label: "KPIs YTD" },
          { key: "upcoming",    icon: Target,     label: "Upcoming / Pre-tender" },
          { key: "competition", icon: Trophy,     label: "Competencia" },
        ].map(m => {
          const Icon = m.icon; const active = tab === m.key;
          return (
            <button key={m.key} onClick={() => setTab(m.key)} data-testid={`oc-tab-${m.key}`}
                    className={`rounded-xl border p-3 text-left transition-all ${active ? "bg-[#007934] text-white border-[#007934] shadow-md" : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200"}`}>
              <div className="flex items-center gap-2"><Icon size={14}/><span className="text-sm font-semibold">{m.label}</span></div>
            </button>
          );
        })}
      </div>
      {/* SU07 · Solvencias es un acceso de apoyo (certificados/referencias),
          no una pestaña comercial más — se mantiene accesible pero fuera
          del orden principal de seis pestañas pedido. */}
      <button onClick={() => setTab("solvencias")} data-testid="oc-tab-solvencias"
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${tab === "solvencias" ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}>
        <ShieldCheck size={12}/> Solvencias (acceso de apoyo)
      </button>

      {tab === "list"        && <OfertasList />}
      {tab === "agenda"      && <AgendaView />}
      {tab === "mercado"     && <MercadoView />}
      {tab === "competition" && <CompetitionMapView />}
      {tab === "kpis"        && <KPIsYTD />}
      {tab === "upcoming"    && <UpcomingList />}
      {tab === "solvencias"  && <SolvenciasCatalogo />}
    </div>
  );
}

// Iter195 · Badge "en limbo" movido a MercadoView.jsx (Iter231, feedback
// usuario: "quitar el botón de Licitaciones y que aparezca en Mercado, con
// acciones para descartar y que se propague al pipeline").

// ═══════════════════════════════════════════════════════════════════════════
// AGENDA — vistas semanal / mensual / trimestral + filtros DG · Actividad
// Iter 124 (Task D): rediseño completo. Antes: sólo calendario mensual sin
// filtros. Ahora: 3 modos de vista + filtro DG cascada + sección aparte de
// "Presentaciones" (evento crítico) + alertas de proximidad.
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// Iter205 · Multi-choice picker de estados para la Agenda
// Popover con checkboxes. Muestra chips resumen con el conteo activo.
// ═══════════════════════════════════════════════════════════════════════════
const AGENDA_ESTADOS_OPTS = [
  { key: "ESTUDIO",      label: "En estudio" },
  { key: "POR DECIDIR",  label: "Por decidir" },
  { key: "PUBLICADA",    label: "Publicadas" },
  { key: "PRESENTADA",   label: "Presentadas" },
  { key: "NO PRESENTADA", label: "No presentadas" },
  { key: "BORRADOR",     label: "Borradores" },
  // Iter230 · Terminales post-pres — antes no eran seleccionables y por
  // tanto los "otros hitos" (adjudicación/firma) de ofertas Suspendida/
  // Anulada/Desistida nunca se podían filtrar desde este picker.
  { key: "SUSPENDIDA",   label: "Suspendidas" },
  { key: "ANULADA",      label: "Anuladas" },
  { key: "DESISTIDA",    label: "Desistidas" },
];
function MultiEstadoPicker({ value, onChange, onReset }) {
  const [open, setOpen] = useState(false);
  const summary = value.length === 0
    ? "Sin estados"
    : value.length === AGENDA_ESTADOS_OPTS.length
      ? "Todos los estados"
      : `${value.length} estado${value.length === 1 ? "" : "s"}`;
  const toggle = (k) => onChange(value.includes(k) ? value.filter(v => v !== k) : [...value, k]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="agenda-filter-estado"
        title="Estados de licitación mostrados en la agenda"
        className={`border rounded-lg px-2 py-1 text-xs bg-white flex items-center gap-1.5 min-w-[130px] justify-between ${
          value.length > 0 && value.length < AGENDA_ESTADOS_OPTS.length
            ? "border-[#007934] text-[#0a5d2f] font-semibold"
            : "border-gray-200 text-gray-700"
        }`}
      >
        <span>{summary}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-56"
               data-testid="agenda-estado-popover">
            {AGENDA_ESTADOS_OPTS.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer"
                     data-testid={`agenda-estado-opt-${opt.key.replace(/\s/g,"_")}`}>
                <input type="checkbox" checked={value.includes(opt.key)} onChange={() => toggle(opt.key)}
                       className="accent-[#007934]"/>
                <span className="text-gray-700">{opt.label}</span>
              </label>
            ))}
            <div className="border-t border-gray-100 mt-1 flex items-center justify-between px-3 py-1.5">
              <button type="button" onClick={onReset}
                className="text-[10px] text-gray-500 hover:text-[#007934]"
                data-testid="agenda-estado-reset">Por defecto</button>
              <button type="button" onClick={() => onChange([])}
                className="text-[10px] text-gray-400 hover:text-rose-600"
                data-testid="agenda-estado-clear">Limpiar todo</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
function AgendaView() {
  // View mode: "week" | "month" | "quarter"
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("oc_agenda_view") || "month"; } catch { return "month"; }
  });
  useEffect(() => { try { localStorage.setItem("oc_agenda_view", viewMode); } catch {} }, [viewMode]);

  // Anchor date (ISO YYYY-MM-DD). Movements shift by unit (7 / 1m / 3m).
  const [anchor, setAnchor] = useState(() => new Date().toISOString().slice(0, 10));

  // DG cascada
  const { user } = useAuth();
  const navigate = useNavigate();
  const { dg: globalDG } = useDGFilter(user?.email);
  // C03 (CRM audit 2026-10-13) · inicializar DESDE el DG global evita el
  // "parpadeo" en que la primera carga iba con "all" (traía TRAT/INDUSTRIAL)
  // antes de que el efecto de sincronización pusiera SERVICIOS — el usuario
  // veía por un instante eventos de otra DG con el filtro SU ya activo.
  const [dgFilter, setDgFilter] = useState(() => globalDG || "all");  // all | SERVICIOS | TRATAMIENTO
  const [actFilter, setActFilter] = useState("all");      // all | C&W | GA | MOB | Light Capex | PPP
  // iter 134 · el pill global (DGFilter en cabecera) tiene prioridad
  useEffect(() => {
    if (globalDG && globalDG !== dgFilter) setDgFilter(globalDG);
    if (!globalDG && dgFilter !== "all") setDgFilter("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalDG]);
  useEffect(() => {
    if (dgFilter === "all") return;
    const allowed = ACTIVIDADES_POR_DG[dgFilter] || [];
    if (actFilter !== "all" && !allowed.includes(actFilter)) setActFilter("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dgFilter]);

  // Toggle: separar presentaciones del resto (default true)
  const [separatePres, setSeparatePres] = useState(true);
  // Iter192/201 · Filtro por estado_licitacion.
  // Iter205 · Multi-choice: el usuario puede combinar estados libremente.
  // Iter230 · Feedback usuario ("está para marcar pero no funciona"): antes
  //   el filtro SÓLO afectaba a "Presentaciones", nunca a "Otros hitos"
  //   (apertura/adjudicación/firma), así que marcar/desmarcar Presentadas o
  //   Suspendidas no cambiaba nada visible ahí. Ahora aplica a AMBOS bloques
  //   por igual. Default = todos los estados marcados (nada se esconde hasta
  //   que el usuario decide ocultar algo explícitamente).
  const ESTADOS_ACTIVAS_DEFAULT = AGENDA_ESTADOS_OPTS.map(o => o.key);
  const [estadoFilters, setEstadoFilters] = useState(() => {
    try {
      // Iter230 · Clave nueva (v2): el filtro ahora también gobierna "Otros
      // hitos", así que reseteamos a "todo visible" para no ocultar de golpe
      // adjudicaciones/firmas a usuarios con el viejo default de 3 estados
      // guardado (que antes sólo afectaba a Presentaciones).
      const stored = localStorage.getItem("oc_agenda_estado_multi_v2");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
      return ESTADOS_ACTIVAS_DEFAULT;
    } catch { return ESTADOS_ACTIVAS_DEFAULT; }
  });
  useEffect(() => {
    try { localStorage.setItem("oc_agenda_estado_multi_v2", JSON.stringify(estadoFilters)); } catch { /* noop */ }
  }, [estadoFilters]);
  const toggleEstado = (est) => {
    setEstadoFilters(prev => prev.includes(est) ? prev.filter(e => e !== est) : [...prev, est]);
  };
  // Iter201 · Estados que NUNCA deben aparecer en presentaciones futuras (ya se cerraron)
  const ESTADOS_CERRADAS_PRE = ["NO PRESENTADA", "ANULADA", "DESISTIDA", "SUSPENDIDA"];

  // Iter227 · Toggles independientes por categoría de evento. El filtro de
  // estados de arriba SÓLO aplica a "Presentaciones" (los otros hitos son
  // inherentemente post-presentación y quedan ocultos si estadoFilters no
  // incluye PRESENTADA/SUSPENDIDA…). Con estos toggles el usuario decide qué
  // categorías ver, sin depender del multi-picker de estados.
  const [showPresentaciones, setShowPresentaciones] = useState(() => {
    try { return JSON.parse(localStorage.getItem("oc_agenda_show_pres") ?? "true"); } catch { return true; }
  });
  const [showOtros, setShowOtros] = useState(() => {
    try { return JSON.parse(localStorage.getItem("oc_agenda_show_otros") ?? "true"); } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("oc_agenda_show_pres", JSON.stringify(showPresentaciones)); } catch { /* noop */ }
  }, [showPresentaciones]);
  useEffect(() => {
    try { localStorage.setItem("oc_agenda_show_otros", JSON.stringify(showOtros)); } catch { /* noop */ }
  }, [showOtros]);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipo, setEquipo] = useState([]);
  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/autocomplete`)
      .then(r => setEquipo(r.data?.equipo?.length ? r.data.equipo : (r.data?.responsables || [])))
      .catch(() => {});
  }, []);
  const assignResponsable = async (ofertaId, val) => {
    try {
      await axios.patch(`${API}/ofertas-comercial/${ofertaId}/responsable`, { responsables: val });
      setEvents(prev => prev.map(ev => ev.oferta_id === ofertaId ? { ...ev, responsable: val } : ev));
      toast.success(val ? "Responsable asignado" : "Responsable quitado");
    } catch { toast.error("No se pudo asignar"); }
  };

  // Compute range from viewMode + anchor
  // Iter232 · Feedback usuario: "mes" mostraba el mes calendario completo
  // (incluía días ya pasados si hoy no es día 1, y se cortaba en fin de mes
  // sin dejar ver el mes siguiente). Ahora todas las vistas (salvo Trimestre)
  // son ventanas RODANTES desde `anchor` hacia adelante, más fáciles de leer:
  // Hoy · Próximos 7 días · Próximo mes (mes + 2 semanas, así si hoy es 5 de
  // octubre se ve hasta el 19 de noviembre — cruza octubre y noviembre).
  const { start, end, rangeLabel } = useMemo(() => {
    const d = new Date(anchor);
    const fmt = (dt) => dt.toISOString().slice(0, 10);
    const fmtShort = (dt) => dt.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
    if (viewMode === "day") {
      return {
        start: fmt(d), end: fmt(d),
        rangeLabel: d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" }),
      };
    }
    if (viewMode === "week") {
      // Rolling: próximos 7 días desde el anchor (no semana de calendario)
      const to = new Date(d); to.setDate(d.getDate() + 6);
      return {
        start: fmt(d), end: fmt(to),
        rangeLabel: `${fmtShort(d)} – ${to.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}`,
      };
    }
    if (viewMode === "quarter") {
      const q = Math.floor(d.getMonth() / 3);        // 0..3
      const qStart = new Date(d.getFullYear(), q * 3, 1);
      const qEnd = new Date(d.getFullYear(), q * 3 + 3, 0);
      return {
        start: fmt(qStart), end: fmt(qEnd),
        rangeLabel: `T${q + 1} ${d.getFullYear()} (${qStart.toLocaleDateString("es-ES", { month: "short" })} – ${qEnd.toLocaleDateString("es-ES", { month: "short" })})`,
      };
    }
    // month → "próximo mes" rolling: mes + 2 semanas desde el anchor
    const to = new Date(d); to.setMonth(d.getMonth() + 1); to.setDate(to.getDate() + 14);
    return {
      start: fmt(d), end: fmt(to),
      rangeLabel: `${fmtShort(d)} – ${to.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}`,
    };
  }, [viewMode, anchor]);

  const shift = (dir) => {
    const d = new Date(anchor);
    if (viewMode === "day")         d.setDate(d.getDate() + dir);
    else if (viewMode === "week")    d.setDate(d.getDate() + dir * 7);
    else if (viewMode === "quarter") d.setMonth(d.getMonth() + dir * 3);
    else                        d.setMonth(d.getMonth() + dir);
    setAnchor(d.toISOString().slice(0, 10));
  };

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ start, end });
    if (dgFilter && dgFilter !== "all")   params.append("direccion_general", dgFilter);
    if (actFilter && actFilter !== "all") params.append("actividad", actFilter);
    axios.get(`${API}/ofertas-comercial/agenda/eventos?${params.toString()}`)
      .then(r => setEvents(r.data || []))
      .catch(() => toast.error("Error"))
      .finally(() => setLoading(false));
  }, [start, end, dgFilter, actFilter]);

  const todayISO = new Date().toISOString().slice(0, 10);

  // Iter154 · Agenda muestra sólo eventos FUTUROS (fecha >= hoy). Las
  // presentaciones pasadas de ofertas que quedaron en ESTUDIO/BORRADOR sin
  // transicionar ensuciaban la vista. Si el usuario navega a un mes anterior
  // no verá nada — deseado: la agenda es forward-looking.
  const futureEvents = useMemo(() => {
    let list = events.filter(e => (e.date || "") >= todayISO);
    // Iter230 · Feedback usuario: el filtro de estados ahora aplica por
    // igual a Presentaciones Y Otros hitos (antes sólo afectaba al primer
    // bloque, y marcar/desmarcar "Presentadas"/"Suspendidas" no tenía
    // ningún efecto visible en Otros hitos, lo que parecía un filtro roto).
    if (estadoFilters.length > 0) {
      const wanted = new Set(estadoFilters.map(s => s.toUpperCase()));
      list = list.filter(e => {
        const est = String(e.estado || "").toUpperCase();
        // Normaliza variantes (SUSPENDIDA_REACTIVABLE/_NO_REACTIVABLE → SUSPENDIDA)
        const norm = est.startsWith("SUSPENDIDA") ? "SUSPENDIDA" : est;
        return wanted.has(norm);
      });
    }
    // Iter227 · Toggles por categoría (Presentaciones / Otros hitos)
    if (!showPresentaciones) {
      list = list.filter(e => e.categoria !== "presentacion" && e.type !== "fecha_presentacion");
    }
    if (!showOtros) {
      list = list.filter(e => e.categoria === "presentacion" || e.type === "fecha_presentacion");
    }
    // Además, en el bloque de presentaciones (sea cual sea el filtro elegido)
    // excluimos las cerradas pre-presentación, porque son un dato incorrecto
    // en un listado "de próximas presentaciones".
    list = list.filter(e => {
      if (e.type !== "fecha_presentacion") return true;
      const est = String(e.estado || "").toUpperCase();
      return !ESTADOS_CERRADAS_PRE.includes(est);
    });
    return list;
  }, [events, todayISO, estadoFilters, showPresentaciones, showOtros]);

  // Split presentaciones vs rest
  // Iter192 · Todas las presentaciones (fecha_presentacion) van al bloque
  // "Presentaciones" independientemente del estado, para que las de hoy
  // no se mezclen con "otros hitos". El resto de fechas (apertura, firma,
  // límite alegaciones, inicio, ...) van a "otros hitos".
  const presentaciones = separatePres
    ? futureEvents.filter(e => e.type === "fecha_presentacion")
    : [];
  const others = separatePres
    ? futureEvents.filter(e => e.type !== "fecha_presentacion")
    : futureEvents;
  // Feedback David (2026-06): separa las presentaciones PENDIENTES (vencimiento
  // real) de las YA PRESENTADAS (estado PRESENTADA), que siguen saliendo pero
  // marcadas y fuera del recuento crítico.
  const yaPres = (e) => String(e.estado || "").toUpperCase() === "PRESENTADA";
  const presentacionesPend = presentaciones.filter(e => !yaPres(e));
  const presentacionesDone = presentaciones.filter(e => yaPres(e));

  // Group by date
  const groupByDate = (list) => {
    const m = {};
    for (const e of list) { (m[e.date] = m[e.date] || []).push(e); }
    return Object.entries(m).sort();
  };

  const daysUntil = (dateStr) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      const today = new Date(new Date().toDateString());
      return Math.round((d - today) / (1000 * 60 * 60 * 24));
    } catch { return null; }
  };
  const urgencyBadge = (dateStr) => {
    const n = daysUntil(dateStr);
    if (n == null) return null;
    if (n < 0)  return { cls: "bg-gray-200 text-gray-500", label: `hace ${Math.abs(n)}d` };
    if (n === 0) return { cls: "bg-rose-500 text-white animate-pulse font-bold", label: "HOY" };
    if (n <= 3)  return { cls: "bg-rose-100 text-rose-700 font-bold", label: `en ${n}d` };
    if (n <= 7)  return { cls: "bg-amber-100 text-amber-700 font-semibold", label: `en ${n}d` };
    return { cls: "bg-gray-100 text-gray-600", label: `en ${n}d` };
  };

  const renderEvent = (e) => {
    const ub = urgencyBadge(e.date);
    // Feedback David (2026-06): una presentación de una oferta YA PRESENTADA no
    // es un vencimiento pendiente → se muestra con marca "Ya presentada" y sin
    // urgencia (no roja), para que no genere falsa alarma pero siga trazable.
    const isYaPres = e.type === "fecha_presentacion" && String(e.estado || "").toUpperCase() === "PRESENTADA";
    // C03 · `auto-import@system` es procedencia técnica (28 ofertas lo llevan
    // en `responsables`), NO una persona a la que asignar trabajo. Se muestra
    // como "Sin responsable" en vez de como un responsable real.
    const rawResp = (e.responsable || "").trim();
    const isSystemResp = !rawResp || /auto-import|@system|^system$/i.test(rawResp);
    return (
      <div
        key={e.id}
        onClick={() => e.oferta_id && navigate(`/ofertas-comercial?tab=list&open=${e.oferta_id}`)}
        onKeyDown={(ev) => { if ((ev.key === "Enter" || ev.key === " ") && e.oferta_id) { ev.preventDefault(); navigate(`/ofertas-comercial?tab=list&open=${e.oferta_id}`); } }}
        role="button"
        tabIndex={0}
        title="Abrir expediente"
        data-testid={`agenda-event-open-${e.oferta_id || "x"}-${e.type || "ev"}`}
        className="flex items-center gap-3 text-xs py-1.5 px-2 rounded hover:bg-emerald-50 cursor-pointer">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: isYaPres ? "#9ca3af" : e.color }}/>
        <span className="font-semibold text-gray-700 w-32 flex-shrink-0">{e.label}</span>
        {isYaPres ? (
          <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold whitespace-nowrap" data-testid={`agenda-ya-presentada-${e.oferta_id}`}><Check size={9}/> Ya presentada</span>
        ) : (ub && <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded-full ${ub.cls}`}>{ub.label}</span>)}
        <span className="font-semibold text-gray-900 min-w-[120px] max-w-[180px] truncate">{e.cliente || "—"}</span>
        {e.direccion_general && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
            e.direccion_general === "SERVICIOS" ? "bg-[#007934]/10 text-[#0a5d2f]" : "bg-purple-100 text-purple-700"
          }`}>{e.direccion_general === "SERVICIOS" ? "SRV" : "TRAT"}</span>
        )}
        {e.actividad_comercial && (
          <span className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{e.actividad_comercial}</span>
        )}
        <span className="text-gray-500 flex-1 truncate">{e.objeto}</span>
        {e.presupuesto_mm != null && (
          <span className="font-mono text-[10px] text-[#007934] font-semibold">{Number(e.presupuesto_mm).toFixed(2)} M€</span>
        )}
        <span onClick={(ev) => ev.stopPropagation()} className="flex-shrink-0">
          {isSystemResp ? (
            <ResponsablePicker value="" equipo={equipo} onChange={(val) => assignResponsable(e.oferta_id, val)} variant="chip" placeholder="Sin responsable" testid={`agenda-responsable-${e.oferta_id}-${e.type || "ev"}`} />
          ) : (
            <ResponsablePicker value={rawResp} equipo={equipo} onChange={(val) => assignResponsable(e.oferta_id, val)} variant="chip" testid={`agenda-responsable-${e.oferta_id}-${e.type || "ev"}`} />
          )}
        </span>
      </div>
    );
  };

  const jumpToday = () => setAnchor(new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-4" data-testid="agenda-view">
      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        {/* View mode segmented. Iter232 · feedback usuario: opciones más
            claras y ventanas rodantes desde hoy (no mes de calendario fijo) */}
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-200">
          {[
            { key: "day",     label: "Hoy" },
            { key: "week",    label: "Próx. 7 días" },
            { key: "month",   label: "Próx. mes" },
            { key: "quarter", label: "Trimestre" },
          ].map(v => (
            <button
              key={v.key}
              data-testid={`agenda-view-${v.key}`}
              onClick={() => setViewMode(v.key)}
              className={`text-[11px] px-3 py-1.5 font-semibold ${
                viewMode === v.key
                  ? "bg-[#007934] text-white"
                  : "bg-white hover:bg-gray-50 text-gray-600"
              }`}>{v.label}</button>
          ))}
        </div>

        <button onClick={() => shift(-1)} className="px-2 py-1 border rounded text-xs hover:bg-gray-50" data-testid="agenda-prev">←</button>
        <button onClick={jumpToday} className="px-2 py-1 border rounded text-xs hover:bg-gray-50 font-semibold text-[#007934]" data-testid="agenda-today">Hoy</button>
        <button onClick={() => shift(1)}  className="px-2 py-1 border rounded text-xs hover:bg-gray-50" data-testid="agenda-next">→</button>

        <span className="text-xs font-semibold text-gray-700 min-w-[140px]" data-testid="agenda-range-label">
          {rangeLabel}
        </span>

        <div className="border-l border-gray-200 h-6 mx-1"/>

        <select
          data-testid="agenda-filter-dg"
          value={dgFilter}
          onChange={e => setDgFilter(e.target.value)}
          className={`border rounded-lg px-2 py-1 text-xs bg-white ${
            dgFilter !== "all" ? "border-[#007934] text-[#0a5d2f] font-semibold" : "border-gray-200"
          }`}
          title="Filtrar por Dirección General">
          <option value="all">Toda DG</option>
          <option value="SERVICIOS">DG Servicios</option>
          <option value="TRATAMIENTO">DG Tratamiento</option>
        </select>
        <select
          data-testid="agenda-filter-actividad"
          value={actFilter}
          onChange={e => setActFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
          <option value="all">Toda actividad</option>
          {(dgFilter === "all" ? ALL_ACTIVIDADES : (ACTIVIDADES_POR_DG[dgFilter] || []))
            .map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={separatePres}
            onChange={e => setSeparatePres(e.target.checked)}
            data-testid="agenda-separate-pres"
            className="accent-[#007934]"/>
          Separar Presentaciones
        </label>

        {/* Iter192/201/205 · Filtro multi-choice por estado_licitacion.
            Default = ESTUDIO+POR DECIDIR+PUBLICADA (agenda forward-looking).
            El usuario puede activar/desactivar libremente cada estado.
            Iter227 · Este filtro sólo afecta a "Presentaciones". Para los
            "otros hitos" usa los pills de al lado. */}
        <MultiEstadoPicker
          value={estadoFilters}
          onChange={setEstadoFilters}
          onReset={() => setEstadoFilters(ESTADOS_ACTIVAS_DEFAULT)}
        />

        {/* Iter227 · Pill toggles independientes por categoría de hito */}
        <div className="flex items-center gap-1" data-testid="agenda-categoria-pills">
          <button
            type="button"
            onClick={() => setShowPresentaciones(v => !v)}
            data-testid="agenda-toggle-presentaciones"
            title="Mostrar / ocultar los eventos de Presentación"
            className={`border rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all ${
              showPresentaciones
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-white text-gray-400 border-gray-200 line-through"
            }`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 mr-1"/>
            Presentaciones
          </button>
          <button
            type="button"
            onClick={() => setShowOtros(v => !v)}
            data-testid="agenda-toggle-otros"
            title="Mostrar / ocultar Otros hitos (apertura, adjudicación, firma, inicio…)"
            className={`border rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all ${
              showOtros
                ? "bg-blue-50 text-blue-700 border-blue-200"
                : "bg-white text-gray-400 border-gray-200 line-through"
            }`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-1"/>
            Otros hitos
          </button>
        </div>

        {/* iter 126: Export buttons */}
        <button
          onClick={() => {
            const params = new URLSearchParams({ start, end });
            if (dgFilter !== "all")  params.append("direccion_general", dgFilter);
            if (actFilter !== "all") params.append("actividad", actFilter);
            axios.get(`${API}/ofertas-comercial/agenda/export.xlsx?${params.toString()}`, { responseType: "blob" })
              .then(r => {
                const url = window.URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `agenda_${start}_${end}.xlsx`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success("Excel descargado");
              })
              .catch(e => toast.error("Error export: " + (e.response?.data?.detail || e.message)));
          }}
          data-testid="agenda-export-xlsx"
          title="Exportar rango filtrado a Excel"
          className="text-[11px] border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg px-2 py-1 font-semibold flex items-center gap-1">
          <FileSpreadsheet size={11}/> XLSX
        </button>
        <button
          onClick={() => {
            const params = new URLSearchParams({ start, end });
            if (dgFilter !== "all")  params.append("direccion_general", dgFilter);
            if (actFilter !== "all") params.append("actividad", actFilter);
            axios.get(`${API}/ofertas-comercial/agenda/export.ics?${params.toString()}`, { responseType: "blob" })
              .then(r => {
                const url = window.URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `agenda_${start}_${end}.ics`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success("ICS descargado (Google/Outlook)");
              })
              .catch(e => toast.error("Error export: " + (e.response?.data?.detail || e.message)));
          }}
          data-testid="agenda-export-ics"
          title="Exportar como iCalendar (Google Calendar / Outlook)"
          className="text-[11px] border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg px-2 py-1 font-semibold flex items-center gap-1">
          <Calendar size={11}/> ICS
        </button>

        <span className="text-xs text-gray-500 ml-auto flex items-center gap-1.5" data-testid="agenda-events-count">
          {(() => {
            const visibles = presentaciones.length + others.length;
            const ocultos = events.length - visibles;
            return (
              <>
                <b className="text-gray-700">{visibles}</b> mostrados
                {ocultos > 0 && (
                  <span className="text-gray-400" title="Eventos del rango que no se ven aquí: ya pasados (la agenda mira hacia adelante) u ocultos por los filtros de estado / categoría de arriba.">
                    · {ocultos} ocultos de {events.length} en el rango
                  </span>
                )}
              </>
            );
          })()}
        </span>
      </div>

      {loading ? (
        <div className="text-center text-sm text-gray-500"><Loader2 className="animate-spin inline mr-2" size={14}/>Cargando…</div>
      ) : (
        <>
          {/* Presentaciones (crítico, si toggle activo) */}
          {separatePres && (
            <div className="bg-gradient-to-br from-rose-50 to-white rounded-xl border-2 border-rose-200 overflow-hidden" data-testid="agenda-presentaciones-section">
              <div className="bg-rose-500 text-white px-4 py-2 flex items-center gap-2">
                <AlertTriangle size={14}/>
                <span className="text-xs font-bold uppercase tracking-wide">Presentaciones ({presentacionesPend.length})</span>
                <span className="text-[10px] opacity-90 ml-2">Fecha límite de entrega — hitos críticos</span>
                {presentacionesDone.length > 0 && (
                  <span className="text-[10px] opacity-90 ml-auto">{presentacionesDone.length} ya presentada{presentacionesDone.length !== 1 ? "s" : ""}</span>
                )}
              </div>
              <div className="divide-y divide-rose-100">
                {groupByDate(presentacionesPend).map(([d, evs]) => (
                  <div key={d} className={`p-2 ${d === todayISO ? "bg-rose-50" : ""}`}>
                    <div className="text-[11px] font-bold text-rose-700 mb-1 flex items-center gap-2">
                      {new Date(d + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                      {d === todayISO && <span className="text-[9px] bg-rose-600 text-white px-1.5 py-0.5 rounded font-bold">HOY</span>}
                    </div>
                    <div className="space-y-0.5">{evs.map(renderEvent)}</div>
                  </div>
                ))}
                {!presentacionesPend.length && (
                  <div className="p-4 text-center text-rose-400 text-xs italic">Sin presentaciones pendientes en este rango</div>
                )}
              </div>
              {presentacionesDone.length > 0 && (
                <div className="border-t border-rose-100 bg-gray-50/70" data-testid="agenda-ya-presentadas-section">
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <Check size={11}/> Ya presentadas ({presentacionesDone.length}) · ya no requieren acción
                  </div>
                  <div className="divide-y divide-gray-100 opacity-75">
                    {groupByDate(presentacionesDone).map(([d, evs]) => (
                      <div key={d} className="p-2">
                        <div className="text-[11px] font-semibold text-gray-500 mb-1">
                          {new Date(d + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                        </div>
                        <div className="space-y-0.5">{evs.map(renderEvent)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Resto de eventos (aperturas, adjudicaciones, firmas, inicio de servicio…) */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 flex items-center gap-2 border-b border-gray-100">
              <Calendar size={14} className="text-gray-500"/>
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                {separatePres ? `Otros hitos (${others.length})` : `Agenda (${events.length})`}
              </span>
              <span className="text-[10px] text-gray-500 ml-2">Aperturas · Adjudicaciones · Firmas · Inicio de servicio · Recursos / Alegaciones</span>
            </div>
            <div className="divide-y divide-gray-100">
              {groupByDate(others).map(([d, evs]) => (
                <div key={d} className={`p-3 ${d === todayISO ? "bg-emerald-50/40" : ""}`}>
                  <div className="text-xs font-bold text-[#007934] mb-2 flex items-center gap-2">
                    {new Date(d + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                    {d === todayISO && <span className="text-[9px] bg-[#007934] text-white px-1.5 py-0.5 rounded font-bold">HOY</span>}
                  </div>
                  <div className="space-y-0.5">{evs.map(renderEvent)}</div>
                </div>
              ))}
              {!others.length && (
                <div className="p-8 text-center text-gray-400 text-sm">Sin otros hitos en este rango</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ImportKpi({ label, value, color = "text-gray-900" }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-2">
      <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide">{label}</div>
      <div className={`text-lg font-bold mt-0.5 font-['Outfit'] ${color}`}>{value}</div>
    </div>
  );
}

function ExcelVerification() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/excel-verification`)
      .then(r => setData(r.data))
      .catch(e => toast.error("Verificación: " + (e.response?.data?.detail || e.message)));
  }, []);

  // Upload Excel maestro
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [forceState, setForceState] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [progress, setProgress] = useState(null); // { phase, processed, total, detail }
  const [showHist, setShowHist] = useState(false); // import de año anterior (histórico)

  const PHASE_LABELS = {
    subiendo: "Subiendo fichero",
    leyendo: "Abriendo el Excel",
    parseando: "Leyendo filas",
    buscando_coincidencias: "Buscando coincidencias en BD",
    preparando: "Preparando cambios",
    escribiendo: "Guardando en BD",
    completado: "Completado",
    error: "Error",
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/import-excel/history?limit=10`);
      setHistory(r.data?.items || []);
    } catch (e) {
      toast.error("Histórico: " + (e.response?.data?.detail || e.message));
    }
  };

  const onUpload = async () => {
    if (!file) { toast.error("Selecciona un archivo .xlsm"); return; }
    setUploading(true);
    setImportResult(null);
    const jobId = (window.crypto?.randomUUID?.() || `job-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setProgress({ phase: "subiendo", processed: 0, total: 0, detail: "Subiendo fichero…" });

    // Polling en paralelo a la petición POST — el event loop del backend
    // intercala ambas peticiones porque la importación tiene puntos await.
    let polling = true;
    const poll = async () => {
      while (polling) {
        try {
          const r = await axios.get(`${API}/ofertas-comercial/import-excel/progress/${jobId}`);
          if (r.data && r.data.phase !== "unknown") setProgress(r.data);
        } catch (_) { /* ignore errores de polling */ }
        await new Promise(res => setTimeout(res, 700));
      }
    };
    poll();

    const fd = new FormData();
    fd.append("file", file);
    fd.append("dry", String(dryRun));
    fd.append("force_state_only", String(forceState));
    fd.append("job_id", jobId);
    try {
      const r = await axios.post(`${API}/ofertas-comercial/import-excel`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setImportResult(r.data);
      const s = r.data?.stats || {};
      toast.success(
        dryRun
          ? `Simulación: ${s.updated} actualizadas, ${s.inserted} nuevas`
          : `Importación: ${s.updated} actualizadas, ${s.inserted} nuevas`
      );
      // refresh history if open
      if (showHistory) loadHistory();
    } catch (e) {
      toast.error("Importación: " + (e.response?.data?.detail || e.message));
    } finally {
      polling = false;
      setUploading(false);
      setProgress(null);
    }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  const cov = data.total_coverage_pct;
  const covColor = cov >= 100 ? "text-green-700" : cov >= 90 ? "text-amber-700" : "text-rose-700";
  const fmtN = (n) => new Intl.NumberFormat("es-ES").format(n || 0);

  return (
    <div className="space-y-4" data-testid="excel-verification">
      {showHist && <HistoricoImportModal onClose={() => setShowHist(false)} onImported={() => toast.success("Año anterior importado. Míralo en YTD Board → Comparativa interanual.")} />}
      {/* === Importador Excel maestro === */}
      <div className="bg-white border-2 border-[#007934]/30 rounded-xl p-5 shadow-sm" data-testid="excel-import-panel">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#007934]/10 flex items-center justify-center flex-shrink-0">
            <Globe size={20} className="text-[#007934]"/>
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 font-['Outfit'] text-base">Importar Excel maestro</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Sube el archivo <code className="bg-gray-100 px-1 rounded">Para Plataforma CONTRATACIÓN_*.xlsm</code>.
              Se importa la hoja <code className="bg-gray-100 px-1 rounded">Market_SU_26</code> haciendo upsert por <strong>expediente</strong>.
            </p>
          </div>
        </div>

        {/* Aviso: años anteriores van por otro flujo (no pisan el dato vivo) */}
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-[11px] text-amber-800" data-testid="import-anio-anterior-note">
          <span className="font-semibold shrink-0">Año en curso (2026).</span>
          <span>
            Esta pantalla importa el ejercicio en curso (hoja <code className="bg-white/60 px-1 rounded">Market_SU_26</code>).
            ¿Vas a subir <strong>2025 o años anteriores</strong>? Usa el botón de abajo: se guardan aparte para las comparativas y <strong>no pisan</strong> el dato vivo.
            <button onClick={() => setShowHist(true)} data-testid="import-anio-anterior-btn"
              className="ml-1 underline font-semibold hover:text-amber-900">Importar año anterior →</button>
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Archivo Excel (.xlsm / .xlsx)</label>
            <input
              type="file"
              accept=".xlsm,.xlsx"
              onChange={e => { setFile(e.target.files?.[0] || null); setImportResult(null); }}
              className="block w-full text-sm border border-gray-200 rounded-lg p-2 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#007934]/10 file:text-[#007934] hover:file:bg-[#007934]/20"
              data-testid="excel-import-file-input"
              disabled={uploading}
            />
          </div>
          <button
            onClick={onUpload}
            disabled={uploading || !file}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#007934] text-white rounded-lg text-sm font-semibold hover:bg-[#006027] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors h-fit"
            data-testid="excel-import-submit">
            {uploading ? <><Loader2 size={14} className="animate-spin"/> Procesando…</> : <><Send size={14}/> {dryRun ? "Simular" : "Importar"}</>}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} disabled={uploading} data-testid="excel-import-dry"/>
            <span className="text-gray-700">Simulación (no escribe en BD)</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={forceState} onChange={e => setForceState(e.target.checked)} disabled={uploading} data-testid="excel-import-force-state"/>
            <span className="text-gray-700">Solo actualizar estado + fecha presentación (no toca otros campos)</span>
          </label>
          <button
            onClick={() => { setShowHistory(s => !s); if (!showHistory) loadHistory(); }}
            className="ml-auto text-indigo-600 hover:underline font-semibold"
            data-testid="excel-import-history-toggle">
            {showHistory ? "Ocultar histórico" : "Ver histórico"}
          </button>
        </div>

        {/* Barra de progreso en vivo */}
        {uploading && progress && (
          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="excel-import-progress">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-700">
                {PHASE_LABELS[progress.phase] || progress.phase}
                {progress.total > 0 && ` · ${progress.processed}/${progress.total}`}
              </span>
              <span className="text-[11px] text-gray-500">{progress.detail}</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${progress.phase === "error" ? "bg-rose-500" : "bg-[#007934]"}`}
                style={{
                  width: progress.total > 0
                    ? `${Math.min(100, Math.round((progress.processed / progress.total) * 100))}%`
                    : (progress.phase === "completado" ? "100%" : "35%"),
                }}
                data-testid="excel-import-progress-bar"
              />
            </div>
          </div>
        )}

        {/* Resultado de la importación */}
        {importResult && (
          <div className={`mt-4 rounded-lg border p-4 ${importResult.dry ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="excel-import-result">
            <div className="flex items-center gap-2 mb-3">
              {importResult.dry
                ? <AlertTriangle size={16} className="text-amber-700"/>
                : <CheckCircle2 size={16} className="text-emerald-700"/>}
              <span className={`font-bold text-sm ${importResult.dry ? "text-amber-900" : "text-emerald-900"}`}>
                {importResult.dry ? "Simulación completada (no se ha escrito en BD)" : "Importación realizada correctamente"}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              <ImportKpi label="Filas leídas"     value={fmtN(importResult.stats?.total_rows)} />
              <ImportKpi label="Actualizadas"     value={fmtN(importResult.stats?.updated)} color="text-emerald-700"/>
              <ImportKpi label="Nuevas"           value={fmtN(importResult.stats?.inserted)} color="text-indigo-700"/>
              <ImportKpi label="Sin expediente"   value={fmtN(importResult.stats?.skipped_no_match_no_expediente)} color="text-amber-700"/>
              <ImportKpi label="Errores"          value={fmtN(importResult.stats?.errors)} color={(importResult.stats?.errors || 0) > 0 ? "text-rose-700" : "text-gray-700"}/>
            </div>

            {Object.keys(importResult.by_state || {}).length > 0 && (
              <div className="mt-2">
                <p className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1">Estados en el Excel</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(importResult.by_state).sort((a,b) => b[1]-a[1]).map(([s, n]) => (
                    <span key={s} className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-md px-2 py-0.5 text-[11px]">
                      <span className="font-semibold text-gray-700">{s}</span>
                      <span className="text-gray-500">·</span>
                      <span className="font-mono text-gray-900">{n}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!importResult.dry && Object.keys(importResult.by_state_db_after || {}).length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-1">Estado actual en BD</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(importResult.by_state_db_after).sort((a,b) => b[1]-a[1]).map(([s, n]) => (
                    <span key={s} className="inline-flex items-center gap-1 bg-emerald-100 border border-emerald-200 rounded-md px-2 py-0.5 text-[11px]">
                      <span className="font-semibold text-emerald-900">{s}</span>
                      <span className="text-emerald-600">·</span>
                      <span className="font-mono text-emerald-900">{n}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(importResult.errors || []).length > 0 && (
              <details className="mt-3">
                <summary className="text-xs font-semibold text-rose-700 cursor-pointer">{importResult.errors.length} errores</summary>
                <ul className="mt-2 space-y-1 text-[11px] text-rose-900 max-h-40 overflow-y-auto">
                  {importResult.errors.map((e, i) => (
                    <li key={i}><code className="bg-white px-1 rounded">Fila {e.row}</code> · {e.cliente} → {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* Histórico */}
        {showHistory && (
          <div className="mt-4 border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="excel-import-history">
            <p className="text-[11px] font-bold text-gray-600 uppercase tracking-wide mb-2">Histórico de importaciones</p>
            {history.length === 0 ? (
              <p className="text-xs text-gray-500">Sin registros.</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 bg-white rounded px-2 py-1.5 text-xs border border-gray-100">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-gray-700 truncate">{h.file}</div>
                      <div className="text-[10px] text-gray-500">{(h.at || "").replace("T", " ").slice(0, 16)} · modo={h.mode}</div>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-mono shrink-0">
                      <span className="text-emerald-700">+{h.stats?.updated || 0} upd</span>
                      <span className="text-indigo-700">+{h.stats?.inserted || 0} new</span>
                      {(h.stats?.errors || 0) > 0 && <span className="text-rose-700">{h.stats.errors} err</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Cobertura comparativa Excel original === */}
      <div className={`rounded-xl border p-4 ${cov >= 100 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="flex items-center gap-3">
          <CheckCircle2 size={20} className={covColor}/>
          <div>
            <div className={`text-2xl font-bold ${covColor}`}>{cov}% cobertura</div>
            <div className="text-xs text-gray-500">Fuente: <code className="bg-white px-1 rounded">{data.source}</code></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left p-3">Hoja Excel</th>
              <th className="text-right p-3">Filas esperadas</th>
              <th className="text-right p-3">Filas en DB</th>
              <th className="text-center p-3">Cobertura</th>
              <th className="text-center p-3">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="p-3 font-semibold">{r.sheet}</td>
                <td className="p-3 text-right font-mono">{r.expected}</td>
                <td className="p-3 text-right font-mono">{r.actual}</td>
                <td className="p-3 text-center font-mono font-bold">{r.coverage_pct}%</td>
                <td className="p-3 text-center">
                  {r.status === "OK"
                    ? <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-[11px] font-bold"><Check size={10}/> OK</span>
                    : <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-700 px-2 py-0.5 rounded text-[11px] font-bold"><AlertTriangle size={10}/> MISSING</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Notas</h4>
        <ul className="space-y-1.5 text-xs text-gray-600">
          {data.notes.map((n, i) => <li key={i} className="flex gap-2"><span className="text-[#007934]">·</span><span>{n}</span></li>)}
        </ul>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
function UpcomingList() {
  const { user } = useAuth();
  const { dg: globalDG, filterByDG } = useDGFilter(user?.email);
  // Iter 124 (Task I): también los CFO y admins pueden mandar
  // el digest a todos. El backend además autoriza a los emails listados en
  // FIXED_DIGEST_RECIPIENTS (Susana / Pablo) por si su rol cambia.
  const isAdmin = user?.role === "admin";
  const canSendAll = ["admin", "cfo"].includes(user?.role);
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [editExtras, setEditExtras] = useState(null);   // {id, client, extra_recipients}
  const [teamEmails, setTeamEmails] = useState([]);

  const load = async () => {
    try { const r = await axios.get(`${API}/ofertas-comercial/upcoming`); setData(r.data); }
    catch { toast.error("Error"); }
  };
  useEffect(() => { load(); }, []);
  // P0-04 (plan cierre POC/Comité 12/10/2026) · "Mi Trabajo" sólo abría la
  // pestaña correcta, nunca aterrizaba en la fila exacta — lee "?open={id}"
  // y hace scroll + resalta la renovación/forecast concreta.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("open");
    if (!id || !data) return;
    const el = document.getElementById(`up-row-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-500");
      setTimeout(() => el.classList.remove("ring-2", "ring-amber-500"), 4000);
    }
  }, [data]);
  useEffect(() => {
    axios.get(`${API}/horas/users`).then(r => setTeamEmails(r.data || [])).catch(() => {});
  }, []);

  // iter 134 · recomputo KPIs y lista tras aplicar filtro DG
  const filteredItems = useMemo(() => filterByDG(data?.items || []), [data, filterByDG]);
  const dgKpis = useMemo(() => {
    if (!globalDG || !data) return null;
    const backlog = filteredItems.reduce((s, i) => s + (Number(i.backlog_total_mm) || 0), 0);
    const hi = filteredItems.filter(i => (i.relevance || 0) >= 4).length;
    return { total: filteredItems.length, backlog_total_mm: backlog, high_relevance: hi };
  }, [globalDG, data, filteredItems]);
  // C04 (CRM audit 2026-10-13) · las previsiones Upcoming/Pre-tender NO llevan
  // Dirección General (0/37), así que un filtro DG las vaciaba a "0 pipelines"
  // mientras los KPIs seguían mostrando el total (474 M€). En vez de mostrar esa
  // contradicción, cuando el filtro DG vacía TODO se avisa de que no es aplicable
  // a previsiones y se muestran las cifras/lista completas del forecast.
  const dgNoAplica = !!globalDG && (data?.items?.length || 0) > 0 && filteredItems.length === 0;
  const tableItems = dgNoAplica ? (data?.items || []) : filteredItems;
  const kpiSrc = dgNoAplica ? data?.kpis : (dgKpis || data?.kpis);

  const doSend = async (toAll) => {
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/ofertas-comercial/upcoming/digest/send-now?to_self_only=${toAll ? "false" : "true"}`
      );
      if (toAll) toast.success(`Digest enviado a ${r.data.count} destinatarios`);
      else toast.success("Resumen enviado a tu email");
    } catch (e) { toast.error("Error enviando digest: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  const doPreview = async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/upcoming/digest/preview`);
      setPreviewHtml(r.data.html);
    } catch (e) { toast.error("Error en preview: " + (e.response?.data?.detail || e.message)); }
  };

  const archiveUpcoming = async (u) => {
    if (!window.confirm(`¿Archivar la previsión "${u.client}"? Esto la elimina de Upcoming/Pre-tender (no afecta a ninguna licitación real ya trackeada).`)) return;
    try {
      await axios.delete(`${API}/ofertas-comercial/upcoming/${u.id}`);
      toast.success("Previsión archivada");
      load();
    } catch (e) { toast.error("Error archivando: " + (e.response?.data?.detail || e.message)); }
  };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="upcoming-list">
      <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="upcoming-valor-potencial-note">
        <Target size={13} className="text-slate-400 mt-0.5 shrink-0"/>
        <span><b className="text-slate-700">Valor potencial de próximas oportunidades</b>: previsiones estimadas de licitaciones aún no publicadas. No es cartera contratada ni un contrato firmado — una previsión puede no materializarse.</span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <Kpi label="Upcoming pipelines" val={kpiSrc.total}/>
        <Kpi label="Valor potencial total (estimado)" val={`${(kpiSrc.backlog_total_mm || 0).toFixed(2)} M€`}/>
        <Kpi label="Valor potencial vigente" val={`${(kpiSrc.vigente_backlog_mm ?? data.kpis.vigente_backlog_mm ?? 0).toFixed(2)} M€`} sub={`${kpiSrc.vigente_count ?? data.kpis.vigente_count} de ${kpiSrc.total} confirmadas`}/>
        <Kpi label="Alta relevancia (4-5)" val={kpiSrc.high_relevance}/>
      </div>
      {(data.kpis.stale_count > 0 || data.kpis.possible_duplicate_count > 0 || data.kpis.same_client_other_service_count > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex flex-wrap items-center gap-4 text-xs" data-testid="upcoming-stale-banner">
          <span className="font-semibold text-amber-800 flex items-center gap-1.5">
            <AlertTriangle size={14}/> Previsión por revisar:
          </span>
          {data.kpis.stale_count > 0 && (
            <span className="text-amber-700" data-testid="upcoming-stale-count">
              <b>{data.kpis.stale_count}</b> con fecha de publicación estimada ya pasada
            </span>
          )}
          {data.kpis.possible_duplicate_count > 0 && (
            <span className="text-amber-700" data-testid="upcoming-duplicate-count">
              <b>{data.kpis.possible_duplicate_count}</b> cuyo <b>cliente y servicio</b> ya coinciden con una licitación real trackeada (posible duplicado)
            </span>
          )}
          {data.kpis.same_client_other_service_count > 0 && (
            <span className="text-slate-500" data-testid="upcoming-other-service-count">
              <b>{data.kpis.same_client_other_service_count}</b> con el mismo cliente pero <b>servicio distinto</b> (no se consideran duplicados)
            </span>
          )}
          <span className="text-amber-600 flex-1">Revisa cada fila marcada y archívala con el botón 🗑 si ya no es relevante — no se fusiona nada por municipio: distingue servicio, lote, contrato y versión antes de descartar.</span>
        </div>
      )}
      {dgNoAplica && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="upcoming-dg-active-note">
          <AlertTriangle size={13} className="text-amber-600 mt-0.5 shrink-0"/>
          <span>El filtro <b>{DG_LABELS[globalDG]}</b> no segmenta las previsiones: Upcoming/Pre-tender aún no lleva Dirección General, así que se muestran las <b>{data.items.length}</b> previsiones completas. Cuando se clasifiquen por DG podrán filtrarse aquí.</span>
        </div>
      )}
      {globalDG && !dgNoAplica && (
        <div className="text-[11px] text-slate-500" data-testid="upcoming-dg-active-note">
          Filtro <b className="text-slate-700">{DG_LABELS[globalDG]}</b> activo · {filteredItems.length} previsiones de esa DG.
        </div>
      )}

      {/* C04 · los envíos/digests son configuración de comunicación, no el centro
          del trabajo sobre oportunidades → plegados por defecto para que la tabla
          de previsiones sea el foco. */}
      <details className="bg-[#007934]/5 border border-[#007934]/20 rounded-xl" data-testid="upcoming-digests-section">
        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-[#0a5d2f] flex items-center gap-2" data-testid="upcoming-digests-toggle">
          <Bell size={14} className="text-[#007934]"/> Envíos y digests programados (configuración)
          <span className="text-[10px] font-normal text-gray-400 ml-auto">clic para desplegar</span>
        </summary>
        <div className="px-3 pb-3 space-y-3">
          {/* Toolbar digest semanal */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-[11px] text-gray-500 flex-1">Digest semanal automático todos los lunes 08:00 · destinatarios extra por fila (icono <Mail size={10} className="inline"/>).</span>
            <button onClick={doPreview} data-testid="digest-preview-btn"
              className="text-xs border border-gray-200 bg-white hover:bg-gray-50 px-3 py-1.5 rounded-lg flex items-center gap-1">
              <Eye size={12}/> Vista previa
            </button>
            <button onClick={() => doSend(false)} disabled={sending} data-testid="digest-send-self-btn"
              className="text-xs border border-[#007934] text-[#007934] bg-white hover:bg-[#007934]/10 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
              {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} Enviarme a mí
            </button>
            {canSendAll && (
              <button onClick={() => doSend(true)} disabled={sending} data-testid="digest-send-all-btn"
                className="text-xs bg-[#007934] text-white hover:bg-[#006528] px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                title={isAdmin ? "Enviar digest a todos los offer_manager/admin/cfo activos" : "Enviar digest a todos los managers (Pablo/Susana/admins)"}>
                {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} Enviar a todos
              </button>
            )}
          </div>
          {/* iter 127/129/133 · recaps y digests de managers */}
          <WeeklyRecapBar canSendAll={canSendAll}/>
          <HomeManagerDigestBar canSendAll={canSendAll}/>
          <PabloDigestBar canSendAll={canSendAll}/>
        </div>
      </details>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] tracking-wide">
              <tr>
                <th className="text-left p-2.5">Cliente</th>
                <th className="text-left p-2.5">Área</th>
                <th className="text-left p-2.5">Actividad</th>
                <th className="text-left p-2.5">Servicio</th>
                <th className="text-right p-2.5">Budget/año</th>
                <th className="text-right p-2.5">Valor pot.</th>
                <th className="text-left p-2.5">Pub. estimada</th>
                <th className="text-left p-2.5">Incumbent</th>
                <th className="text-center p-2.5">Rel.</th>
                <th className="text-center p-2.5">Notificar</th>
                <th className="text-center p-2.5">Revisión</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tableItems.map(u => (
                <tr key={u.id} id={`up-row-${u.id}`} className={`hover:bg-gray-50 transition-all ${u.is_stale || u.possible_duplicate ? "bg-amber-50/40" : ""}`} data-testid={`up-row-${u.id}`}>
                  <td className="p-2.5 font-semibold">
                    {u.client}
                    {u.possible_duplicate && (
                      <span
                        title={`Posible duplicado (${u.possible_duplicate.match_reason || "coincidencia"}): «${(u.possible_duplicate.objeto || "").slice(0, 80)}» · estado ${u.possible_duplicate.estado_licitacion || "—"}`}
                        data-testid={`up-dup-badge-${u.id}`}
                        className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-700 cursor-help"
                      >
                        YA TRACKEADA
                      </span>
                    )}
                  </td>
                  <td className="p-2.5 text-gray-500 max-w-[150px] truncate">{u.management_area}</td>
                  <td className="p-2.5">{u.activity}</td>
                  <td className="p-2.5 text-gray-600 max-w-[300px] truncate">{u.service_description}</td>
                  <td className="p-2.5 text-right font-mono">{u.budget_mm_year?.toFixed(1) || "—"}</td>
                  <td className="p-2.5 text-right font-mono font-bold text-[#007934]">{u.backlog_mm?.toFixed(1) || "—"}</td>
                  <td className="p-2.5">
                    <span className={u.is_stale ? "text-red-600 font-semibold" : "text-gray-500"} data-testid={u.is_stale ? `up-stale-${u.id}` : undefined}>
                      {u.estimated_publication_date || "—"}
                      {u.is_stale && <span className="ml-1 text-[9px] uppercase">· VENCIDA</span>}
                    </span>
                  </td>
                  <td className="p-2.5 text-gray-600 max-w-[150px] truncate">{u.incumbent}</td>
                  <td className="p-2.5 text-center">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      u.relevance >= 5 ? "bg-rose-500 text-white" :
                      u.relevance >= 4 ? "bg-amber-500 text-white" :
                      "bg-gray-300 text-gray-700"}`}>{u.relevance}</span>
                  </td>
                  <td className="p-2.5 text-center">
                    <button
                      onClick={() => setEditExtras({ id: u.id, client: u.client, extra_recipients: u.extra_recipients || [] })}
                      title="Configurar destinatarios extra"
                      data-testid={`up-extras-${u.id}`}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold ${
                        (u.extra_recipients || []).length > 0
                          ? "bg-[#007934] text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <Mail size={10}/> {(u.extra_recipients || []).length || "+"}
                    </button>
                  </td>
                  <td className="p-2.5 text-center">
                    <button
                      onClick={() => archiveUpcoming(u)}
                      title="Archivar esta previsión (ya no es relevante)"
                      data-testid={`up-archive-${u.id}`}
                      className="inline-flex items-center justify-center p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={13}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {previewHtml && (
        <DigestPreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)}/>
      )}
      {editExtras && (
        <ExtraRecipientsModal
          item={editExtras}
          teamEmails={teamEmails}
          onClose={() => setEditExtras(null)}
          onSaved={() => { setEditExtras(null); load(); }}
        />
      )}
    </div>
  );
}

function DigestPreviewModal({ html, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="digest-preview-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-sm font-bold flex items-center gap-2"><Eye size={14}/> Vista previa · Digest semanal</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>
        <iframe title="preview" srcDoc={html} className="w-full flex-1 border-0" style={{ minHeight: "60vh" }}/>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Iter 127 · Weekly Recap Email (Wins/Losses/Upcoming top 3) — lunes 08:30 UTC
// ═══════════════════════════════════════════════════════════════════════════
function WeeklyRecapBar({ canSendAll }) {
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  const doPreview = async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/weekly-recap/preview`);
      setPreviewHtml(r.data.html);
    } catch (e) { toast.error("Preview error: " + (e.response?.data?.detail || e.message)); }
  };

  const doSend = async (all) => {
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/ofertas-comercial/weekly-recap/send-now?to_self_only=${!all}`
      );
      if (r.data.mode === "self") toast.success("Weekly Recap enviado a tu correo");
      else toast.success(`Weekly Recap enviado a ${r.data.count} destinatarios`);
    } catch (e) { toast.error("Send error: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  return (
    <>
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="weekly-recap-bar">
        <TrendingUp size={14} className="text-purple-700"/>
        <span className="text-xs font-semibold text-purple-800">Weekly Recap</span>
        <span className="text-[11px] text-gray-500 flex-1">
          Automático lunes 08:30 UTC · Wins / Losses / Top 3 upcoming · Susana + Pablo + admins/CFO
        </span>
        <button
          onClick={doPreview}
          data-testid="weekly-recap-preview-btn"
          className="text-xs border border-gray-200 bg-white hover:bg-gray-50 px-3 py-1.5 rounded-lg flex items-center gap-1">
          <Eye size={12}/> Vista previa
        </button>
        <button
          onClick={() => doSend(false)}
          disabled={sending}
          data-testid="weekly-recap-send-self-btn"
          className="text-xs border border-purple-500 text-purple-700 bg-white hover:bg-purple-100 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} Enviarme a mí
        </button>
        {canSendAll && (
          <button
            onClick={() => doSend(true)}
            disabled={sending}
            data-testid="weekly-recap-send-all-btn"
            className="text-xs bg-purple-600 text-white hover:bg-purple-700 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
            title="Enviar Weekly Recap a Susana + Pablo + admins/CFO activos">
            {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} Enviar a todos
          </button>
        )}
      </div>
      {previewHtml && (
        <DigestPreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)}/>
      )}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Iter 129 · Home Manager Digest (Upcoming + Recap + CFO Queue en 1 correo)
// ═══════════════════════════════════════════════════════════════════════════
function HomeManagerDigestBar({ canSendAll }) {
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);

  const doPreview = async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/home-manager/preview`);
      setPreviewHtml(r.data.html);
    } catch (e) { toast.error("Preview error: " + (e.response?.data?.detail || e.message)); }
  };
  const doSend = async (all) => {
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/ofertas-comercial/home-manager/send-now?to_self_only=${!all}`
      );
      if (r.data.mode === "self") toast.success("Home Manager Digest enviado a tu correo");
      else toast.success(`Home Manager Digest enviado a ${r.data.count} destinatarios (Susana + Pablo)`);
    } catch (e) { toast.error("Send error: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  return (
    <>
      <div className="bg-slate-900 text-white rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="home-manager-bar">
        <Sparkles size={14} className="text-amber-300"/>
        <span className="text-xs font-semibold">Home Manager Digest</span>
        <span className="text-[11px] text-slate-300 flex-1">
          Consolida Upcoming + Wins/Losses + Cola CFO en 1 correo · Lunes 09:00 UTC · Sólo Susana + Pablo
        </span>
        <button
          onClick={doPreview}
          data-testid="home-manager-preview-btn"
          className="text-xs border border-slate-600 hover:bg-slate-800 px-3 py-1.5 rounded-lg flex items-center gap-1">
          <Eye size={12}/> Vista previa
        </button>
        <button
          onClick={() => doSend(false)}
          disabled={sending}
          data-testid="home-manager-send-self-btn"
          className="text-xs border border-amber-400 text-amber-300 hover:bg-amber-900/40 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} Enviarme a mí
        </button>
        {canSendAll && (
          <button
            onClick={() => doSend(true)}
            disabled={sending}
            data-testid="home-manager-send-all-btn"
            className="text-xs bg-amber-500 hover:bg-amber-600 text-slate-900 px-3 py-1.5 rounded-lg flex items-center gap-1 font-semibold disabled:opacity-50"
            title="Enviar a Susana + Pablo (recipients fijos)">
            {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} Enviar a Susana + Pablo
          </button>
        )}
      </div>
      {previewHtml && (
        <DigestPreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)}/>
      )}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Iter 133 · Pablo Digest (foco en patrones rechazo CFO recurrentes)
// ═══════════════════════════════════════════════════════════════════════════
function PabloDigestBar({ canSendAll }) {
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ofertas-comercial/pablo-digest/preview`)
      .then(r => setKpis(r.data.data))
      .catch(() => {});
  }, []);

  const doPreview = async () => {
    try {
      const r = await axios.get(`${API}/ofertas-comercial/pablo-digest/preview`);
      setPreviewHtml(r.data.html);
      setKpis(r.data.data);
    } catch (e) { toast.error("Preview error: " + (e.response?.data?.detail || e.message)); }
  };
  const doSend = async (toPablo) => {
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/ofertas-comercial/pablo-digest/send-now?to_self_only=${!toPablo}`
      );
      if (r.data.mode === "self") toast.success("Pablo Digest enviado a tu correo (preview)");
      else toast.success("Pablo Digest enviado a Pablo Pérez");
    } catch (e) { toast.error("Send error: " + (e.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  return (
    <>
      <div className="bg-gradient-to-r from-indigo-900 to-blue-900 text-white rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="pablo-digest-bar">
        <Target size={14} className="text-amber-300"/>
        <span className="text-xs font-semibold">Pablo Digest</span>
        <span className="text-[11px] text-indigo-200 flex-1">
          Semanal lunes 09:15 UTC · Foco patrones rechazo CFO 30d
          {kpis && (
            <span className="ml-2 font-semibold text-white">
              {kpis.recurring_categories} recurrentes · {kpis.reject_total_30d} rechazos · {kpis.wins}W/{kpis.losses}L
            </span>
          )}
        </span>
        <button
          onClick={doPreview}
          data-testid="pablo-digest-preview-btn"
          className="text-xs border border-indigo-500 hover:bg-indigo-800 px-3 py-1.5 rounded-lg flex items-center gap-1">
          <Eye size={12}/> Vista previa
        </button>
        <button
          onClick={() => doSend(false)}
          disabled={sending}
          data-testid="pablo-digest-send-self-btn"
          className="text-xs border border-amber-400 text-amber-300 hover:bg-amber-900/30 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50">
          {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} A mi correo
        </button>
        {canSendAll && (
          <button
            onClick={() => doSend(true)}
            disabled={sending}
            data-testid="pablo-digest-send-pablo-btn"
            title="Enviar el digest a Pablo Pérez (destinatario fijo)"
            className="text-xs bg-amber-500 hover:bg-amber-600 text-slate-900 px-3 py-1.5 rounded-lg flex items-center gap-1 font-semibold disabled:opacity-50">
            {sending ? <Loader2 className="animate-spin" size={12}/> : <Send size={12}/>} Enviar a Pablo
          </button>
        )}
      </div>
      {previewHtml && (
        <DigestPreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)}/>
      )}
    </>
  );
}


function ExtraRecipientsModal({ item, teamEmails, onClose, onSaved }) {
  const [selected, setSelected] = useState(item.extra_recipients || []);
  const [customInput, setCustomInput] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (email) => {
    setSelected(selected.includes(email) ? selected.filter(e => e !== email) : [...selected, email]);
  };
  const addCustom = () => {
    const e = customInput.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Email inválido");
    if (selected.includes(e)) return;
    setSelected([...selected, e]);
    setCustomInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ofertas-comercial/upcoming/${item.id}`, { extra_recipients: selected });
      toast.success("Destinatarios guardados");
      onSaved();
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="extras-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <h3 className="text-sm font-bold">Destinatarios extra · {item.client}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Además de ti (como creador), estas personas también recibirán el digest semanal cuando esta oportunidad aparezca.</p>
        </div>

        <div>
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">Del equipo</div>
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
            {teamEmails.map(u => (
              <label key={u.email} className="flex items-center gap-2 text-xs hover:bg-gray-50 rounded p-1.5 cursor-pointer">
                <input type="checkbox" checked={selected.includes(u.email)} onChange={() => toggle(u.email)} data-testid={`extras-cb-${u.email}`}/>
                <span className="font-medium">{u.name || u.email}</span>
                <span className="text-gray-400">· {u.email}</span>
              </label>
            ))}
            {teamEmails.length === 0 && <div className="text-[11px] text-gray-400 p-2">(sin usuarios)</div>}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">Email externo</div>
          <div className="flex gap-2">
            <input
              type="email"
              value={customInput}
              onChange={e => setCustomInput(e.target.value)}
              placeholder="persona@empresa.com"
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-xs"
              data-testid="extras-custom-input"
            />
            <button onClick={addCustom} className="text-xs border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-gray-50" data-testid="extras-custom-add">+ Añadir</button>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selected.map(e => (
              <span key={e} className="inline-flex items-center gap-1 bg-[#007934]/10 text-[#007934] px-2 py-0.5 rounded text-[11px] font-semibold">
                {e}
                <button onClick={() => setSelected(selected.filter(x => x !== e))} className="hover:text-rose-600"><X size={10}/></button>
              </span>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50" data-testid="extras-save">
            {saving && <Loader2 className="animate-spin" size={12}/>} Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
